import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOwner, useDeleteOwner } from '@/hooks/useOwners';
import { usePatients, useDeletePatient } from '@/hooks/usePatients';
import { Patient } from '@/types';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Plus,
  User,
  Phone,
  Mail,
  MapPin,
  FileText,
  Dog,
  Calendar,
  Scale,
} from 'lucide-react';
import OwnerForm from '@/components/OwnerForm';
import PatientForm from '@/components/PatientForm';

export default function OwnerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const ownerId = id ? parseInt(id) : null;

  const { data: owner, isLoading } = useOwner(ownerId);
  const { data: patients } = usePatients({ owner_id: ownerId || undefined });
  const deleteOwner = useDeleteOwner();
  const deletePatient = useDeletePatient();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState<Patient | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<'owner' | Patient | null>(null);

  const handleDeleteOwner = async () => {
    if (!ownerId) return;
    try {
      await deleteOwner.mutateAsync(ownerId);
      navigate('/dashboard/owners');
    } catch (error) {
      console.error('Error deleting owner:', error);
    }
  };

  const handleDeletePatient = async (patient: Patient) => {
    try {
      await deletePatient.mutateAsync(patient.id);
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting patient:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando...</div>
      </div>
    );
  }

  if (!owner) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="text-gray-500 mb-4">Propietario no encontrado</div>
        <Button onClick={() => navigate('/dashboard/owners')}>Volver a Propietarios</Button>
      </div>
    );
  }

  const getFullName = () => `${owner.first_name} ${owner.last_name}`;

  const getSexLabel = (sex: string) => {
    switch (sex) {
      case 'male':
        return 'Macho';
      case 'female':
        return 'Hembra';
      default:
        return 'No especificado';
    }
  };

  const calculateAge = (birthDate: string) => {
    const birth = new Date(birthDate);
    const today = new Date();
    let years = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      years--;
    }
    return years;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/dashboard/owners')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{getFullName()}</h1>
          <p className="text-gray-600">Ficha del propietario</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setIsEditModalOpen(true)}>
            <Edit className="h-4 w-4 mr-2" />
            Editar
          </Button>
          <Button
            variant="danger"
            onClick={() => setDeleteConfirm('owner')}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Eliminar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Información del propietario */}
        <div className="lg:col-span-1">
          <div className="card">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-taupe-100 rounded-full flex items-center justify-center">
                <User className="h-8 w-8 text-taupe-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{getFullName()}</h2>
                {owner.email && <p className="text-gray-500">{owner.email}</p>}
              </div>
            </div>

            <div className="space-y-4">
              {owner.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Teléfono</p>
                    <p className="text-gray-900">{owner.phone}</p>
                  </div>
                </div>
              )}

              {owner.phone_secondary && (
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Teléfono secundario</p>
                    <p className="text-gray-900">{owner.phone_secondary}</p>
                  </div>
                </div>
              )}

              {owner.email && (
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="text-gray-900">{owner.email}</p>
                  </div>
                </div>
              )}

              {owner.address && (
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Dirección</p>
                    <p className="text-gray-900">{owner.address}</p>
                  </div>
                </div>
              )}

              {owner.notes && (
                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Notas</p>
                    <p className="text-gray-900">{owner.notes}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mascotas */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                Mascotas ({patients?.length || 0})
              </h2>
              <Button onClick={() => setIsPatientModalOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Nueva Mascota
              </Button>
            </div>

            {patients && patients.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {patients.map((patient) => (
                  <div
                    key={patient.id}
                    className="border border-gray-200 rounded-xl p-4 hover:border-cream-400 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-cream-100 rounded-full flex items-center justify-center">
                          <Dog className="h-6 w-6 text-cream-700" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{patient.name}</h3>
                          <p className="text-sm text-gray-500">
                            {patient.species} {patient.breed && `- ${patient.breed}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingPatient(patient);
                            setIsPatientModalOpen(true);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirm(patient)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <span className="text-gray-400">Sexo:</span>
                        {getSexLabel(patient.sex)}
                      </div>
                      {patient.birth_date && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          {calculateAge(patient.birth_date)} años
                        </div>
                      )}
                      {patient.weight && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Scale className="h-4 w-4 text-gray-400" />
                          {patient.weight} kg
                        </div>
                      )}
                    </div>

                    {patient.allergies && (
                      <div className="mt-3 px-2 py-1 bg-red-50 text-red-700 text-xs rounded-lg">
                        Alergias: {patient.allergies}
                      </div>
                    )}

                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <Button
                        variant="secondary"
                        size="sm"
                        className="w-full"
                        onClick={() => navigate(`/dashboard/patients?id=${patient.id}`)}
                      >
                        Ver historial médico
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Este propietario no tiene mascotas registradas
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de edición de propietario */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Editar Propietario"
        size="lg"
      >
        <OwnerForm
          owner={owner}
          onSuccess={() => setIsEditModalOpen(false)}
        />
      </Modal>

      {/* Modal de mascota */}
      <Modal
        isOpen={isPatientModalOpen}
        onClose={() => {
          setIsPatientModalOpen(false);
          setEditingPatient(null);
        }}
        title={editingPatient ? 'Editar Mascota' : 'Nueva Mascota'}
        size="lg"
      >
        <PatientForm
          patient={editingPatient}
          ownerId={ownerId!}
          onSuccess={() => {
            setIsPatientModalOpen(false);
            setEditingPatient(null);
          }}
        />
      </Modal>

      {/* Modal de confirmación de eliminación */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title={deleteConfirm === 'owner' ? 'Eliminar Propietario' : 'Eliminar Mascota'}
        size="sm"
      >
        {deleteConfirm === 'owner' ? (
          <div className="space-y-4">
            <p className="text-gray-600">
              ¿Estás seguro de eliminar a <strong>{getFullName()}</strong>?
            </p>
            <p className="text-sm text-red-600">
              Esta acción eliminará también todas las mascotas asociadas y no se puede deshacer.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
                Cancelar
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteOwner}
                loading={deleteOwner.isPending}
              >
                Eliminar
              </Button>
            </div>
          </div>
        ) : deleteConfirm ? (
          <div className="space-y-4">
            <p className="text-gray-600">
              ¿Estás seguro de eliminar a <strong>{deleteConfirm.name}</strong>?
            </p>
            <p className="text-sm text-red-600">
              Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
                Cancelar
              </Button>
              <Button
                variant="danger"
                onClick={() => handleDeletePatient(deleteConfirm)}
                loading={deletePatient.isPending}
              >
                Eliminar
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
